import { z } from 'zod';

/**
 * How a player looks, kept on their profile so it follows them to every game (ADR 0013).
 * The platform only knows the ids; each game draws them its own way.
 */

/** Every player may choose these figures for free. */
export const FREE_FIGURES = [
  'adventurer',
  'princess',
  'strongman',
  'pirate',
  'squire',
  'viking',
  // Drawn in layers, so the player picks its skin, hair, eyes and mouth (2026-10-25).
  'custom',
] as const;

/** The figure a game builds from the player's own choices (`Avatar.look`). */
export const CUSTOM_FIGURE = 'custom';

/** Figures a game can sell. */
export const PAID_FIGURES = [
  'wizard',
  'knight',
  'visorKnight',
  'ghost',
  'cyclops',
  'crab',
  'robot',
  'hooded',
] as const;

export const FIGURE_IDS = [...FREE_FIGURES, ...PAID_FIGURES] as const;
export const HAT_IDS = [
  'cap',
  'strawHat',
  'flowerCrown',
  'pirateHat',
  'shellCrown',
  'pearlCrown',
  'beanie',
  'bow',
  'partyHat',
  'chefHat',
  'cowboyHat',
  'topHat',
  'vikingHelmet',
  'starCrown',
] as const;
/** Worn on the face, with or without a hat (2026-10-25). */
export const FACE_IDS = [
  'glasses',
  'freckles',
  'moustache',
  'eyepatch',
  'sunglasses',
  'beard',
] as const;

/** What a player picks for `CUSTOM_FIGURE`, all free. */
export const SKIN_TONES = ['light', 'fair', 'tan', 'brown', 'dark'] as const;
export const HAIR_STYLES = ['short', 'long', 'curly', 'ponytail', 'braids', 'bald'] as const;
export const HAIR_COLOURS = ['black', 'brown', 'blonde', 'red', 'blue', 'pink'] as const;
export const EYE_STYLES = ['round', 'happy', 'sleepy', 'lashes'] as const;
export const MOUTH_STYLES = ['smile', 'grin', 'open'] as const;

export type FigureId = (typeof FIGURE_IDS)[number];
export type HatId = (typeof HAT_IDS)[number];
export type FaceId = (typeof FACE_IDS)[number];
/** Anything a player can own: a figure, a hat or something for the face. The ids never overlap. */
export type AvatarItemId = FigureId | HatId | FaceId;

export const figureIdSchema = z.enum(FIGURE_IDS);
export const hatIdSchema = z.enum(HAT_IDS);
export const faceIdSchema = z.enum(FACE_IDS);
export const avatarItemIdSchema = z.union([figureIdSchema, hatIdSchema, faceIdSchema]);

export const figureLookSchema = z.object({
  skin: z.enum(SKIN_TONES),
  hair: z.enum(HAIR_STYLES),
  hairColour: z.enum(HAIR_COLOURS),
  eyes: z.enum(EYE_STYLES),
  mouth: z.enum(MOUTH_STYLES),
});
export type FigureLook = z.infer<typeof figureLookSchema>;
export type SkinTone = FigureLook['skin'];
export type HairStyle = FigureLook['hair'];
export type HairColour = FigureLook['hairColour'];
export type EyeStyle = FigureLook['eyes'];
export type MouthStyle = FigureLook['mouth'];
export type LookPart = keyof FigureLook;
export const LOOK_PARTS = ['skin', 'hair', 'hairColour', 'eyes', 'mouth'] as const;
/** The choices for each part of the custom figure, in the order they are browsed. */
export const LOOK_CHOICES: { readonly [Part in LookPart]: readonly FigureLook[Part][] } = {
  skin: SKIN_TONES,
  hair: HAIR_STYLES,
  hairColour: HAIR_COLOURS,
  eyes: EYE_STYLES,
  mouth: MOUTH_STYLES,
};
export const DEFAULT_LOOK: FigureLook = {
  skin: 'fair',
  hair: 'short',
  hairColour: 'brown',
  eyes: 'round',
  mouth: 'smile',
};

/**
 * What the player wears. Profiles saved before 2026-10-25 have only the figure and hat, so the
 * face is bare and the custom figure starts from `DEFAULT_LOOK`.
 */
export const avatarSchema = z.object({
  figure: figureIdSchema,
  hat: hatIdSchema.nullable(),
  face: faceIdSchema.nullable().default(null),
  look: figureLookSchema.default(() => ({ ...DEFAULT_LOOK })),
});
export type Avatar = z.infer<typeof avatarSchema>;

/** What a player owns and wears. `avatar` is `null` until they have chosen. */
export interface Wardrobe {
  owned: AvatarItemId[];
  avatar: Avatar | null;
}

const FREE: ReadonlySet<AvatarItemId> = new Set(FREE_FIGURES);

export function isFreeItem(item: AvatarItemId): boolean {
  return FREE.has(item);
}

/** True when the player may wear this: everything in it is free or owned. */
export function canWear(owned: readonly AvatarItemId[], avatar: Avatar): boolean {
  return paidItems(avatar).every((item) => owned.includes(item));
}

/** The items in an avatar that have to be owned, i.e. not free. */
export function paidItems(avatar: Avatar): AvatarItemId[] {
  const items: AvatarItemId[] = [avatar.figure];
  if (avatar.hat) items.push(avatar.hat);
  if (avatar.face) items.push(avatar.face);
  return items.filter((item) => !isFreeItem(item));
}
